import React, { useState, useEffect } from "react";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/hooks/useAuth";
import { useNavigate } from "react-router-dom";
import {
    Users,
    BarChart3,
    MessageSquare,
    Shield,
    RefreshCw,
    TrendingUp,
    Globe,
    Database,
    Zap,
} from "lucide-react";

interface InsightsData {
    totalProjects: number;
    activeUsers: number;
    languagePairs: {
        source_language: string;
        target_language: string;
        project_count: number;
        total_words: number;
    }[];
    tmStats: {
        total_entries: number;
        projects_with_tm: number;
        added_last_7_days: number;
    };
    preAOStats: {
        orthographic_standard: string;
        project_count: number;
        total_words: number;
    }[];
    usersNearLimit: {
        email: string;
        mt_pct_used: number;
        ai_pct_used: number;
        tier: string;
    }[];
    providerBreakdown: {
        deepl_total: number;
        google_total: number;
        mymemory_total: number;
        grand_total: number;
        deepl_pct: number;
        google_pct: number;
        mymemory_pct: number;
    };
}

const ADMIN_EMAIL = "jpzage@icloud.com";

interface UserRow {
    id: string;
    email: string;
    full_name: string;
    tier: string;
    created_at: string;
}

interface UsageRow {
    user_id: string;
    mt_chars_used: number;
    ai_queries_used: number;
    mt_chars_deepl: number;
    mt_chars_google: number;
    mt_chars_mymemory: number;
    period_start: string;
}

interface FeedbackRow {
    id: string;
    user_id: string;
    message: string;
    page: string;
    created_at: string;
}

const AdminPage: React.FC = () => {
    const { user } = useAuth();
    const navigate = useNavigate();
    const [tab, setTab] = useState<"users" | "usage" | "feedback" | "insights">(
        "users",
    );
    const [profiles, setProfiles] = useState<UserRow[]>([]);
    const [usage, setUsage] = useState<UsageRow[]>([]);
    const [feedback, setFeedback] = useState<FeedbackRow[]>([]);
    const [loading, setLoading] = useState(true);
    const [updating, setUpdating] = useState<string | null>(null);
    const [insights, setInsights] = useState<InsightsData | null>(null);

    useEffect(() => {
        if (!user) return;
        if (user.email !== ADMIN_EMAIL) {
            navigate("/dashboard");
            return;
        }
        loadData();
    }, [user]);

    const loadData = async () => {
        setLoading(true);
        const [p, u, f] = await Promise.all([
            supabase
                .from("profiles")
                .select("*")
                .order("created_at", { ascending: false }),
            supabase.from("user_usage").select("*"),
            supabase
                .from("feedback")
                .select("*")
                .order("created_at", { ascending: false }),
        ]);
        if (p.data) setProfiles(p.data);
        if (u.data) setUsage(u.data);
        if (f.data) setFeedback(f.data);
        await loadInsights();
        setLoading(false);
    };

    const loadInsights = async () => {
        const [projects, tm, preao, limits, providers, profilesData] =
            await Promise.all([
                supabase
                    .from("cat_projects")
                    .select(
                        "owner_id, source_language, target_language, word_count, status",
                    ),
                supabase.from("cat_translation_memory").select("id", { count: "exact", head: true }),
                supabase
                    .from("cat_projects")
                    .select("orthographic_standard, word_count")
                    .neq("orthographic_standard", null),
                supabase
                    .from("user_usage")
                    .select("user_id, mt_chars_used, ai_queries_used, storage_used_bytes"),
                supabase
                    .from("user_usage")
                    .select(
                        "mt_chars_deepl, mt_chars_google, mt_chars_mymemory, mt_chars_used",
                    ),
                supabase.from("profiles").select("id, email"),
            ]);

        // Language pairs
        const pairMap: Record<
            string,
            {
                source_language: string;
                target_language: string;
                project_count: number;
                total_words: number;
            }
        > = {};
        projects.data?.forEach((p) => {
            const key = `${p.source_language?.toLowerCase()}-${p.target_language?.toLowerCase()}`;
            if (!pairMap[key])
                pairMap[key] = {
                    source_language: p.source_language,
                    target_language: p.target_language,
                    project_count: 0,
                    total_words: 0,
                };
            pairMap[key].project_count++;
            pairMap[key].total_words += p.word_count || 0;
        });
        const languagePairs = Object.values(pairMap).sort(
            (a, b) => b.project_count - a.project_count,
        );

        // Pre-AO stats
        const aoMap: Record<
            string,
            {
                orthographic_standard: string;
                project_count: number;
                total_words: number;
            }
        > = {};
        preao.data?.forEach((p) => {
            const std = p.orthographic_standard || "post_ao";
            if (!aoMap[std])
                aoMap[std] = {
                    orthographic_standard: std,
                    project_count: 0,
                    total_words: 0,
                };
            aoMap[std].project_count++;
            aoMap[std].total_words += p.word_count || 0;
        });

        // TM stats - query directly since RPC may not exist
        const tmDirect = await supabase
            .from("cat_translation_memory")
            .select("id, created_at", { count: "exact" });
        const sevenDaysAgo = new Date(
            Date.now() - 7 * 24 * 60 * 60 * 1000,
        ).toISOString();
        const tmRecent = await supabase
            .from("cat_translation_memory")
            .select("id", { count: "exact" })
            .gte("created_at", sevenDaysAgo);
        const tmStats = {
            total_entries: tmDirect.count || 0,
            projects_with_tm: 0,
            added_last_7_days: tmRecent.count || 0,
        };

        // Provider breakdown
        const provData = providers.data || [];
        const deepl_total = provData.reduce(
            (s, r) => s + (r.mt_chars_deepl || 0),
            0,
        );
        const google_total = provData.reduce(
            (s, r) => s + (r.mt_chars_google || 0),
            0,
        );
        const mymemory_total = provData.reduce(
            (s, r) => s + (r.mt_chars_mymemory || 0),
            0,
        );
        const grand_total = provData.reduce(
            (s, r) => s + (r.mt_chars_used || 0),
            0,
        );

        // Users near limit
        const profileMap: Record<string, string> = {};
        const tierMap: Record<string, string> = {};
        profilesData.data?.forEach((p) => {
            profileMap[p.id] = p.email;
            tierMap[p.id] = p.tier || "free";
        });
        const TIER_MT:  Record<string, number> = { free: 50000, standard: 200000, premium: 500000 };
        const TIER_AI:  Record<string, number> = { free: 100,   standard: 500,    premium: 3000   };
        const usersNearLimit = (limits.data || [])
            .filter((u) => {
                const tier = tierMap[u.user_id] || "free";
                return u.mt_chars_used > TIER_MT[tier] * 0.6 || u.ai_queries_used > TIER_AI[tier] * 0.7;
            })
            .map((u) => {
                const tier = tierMap[u.user_id] || "free";
                return {
                    email: profileMap[u.user_id] || u.user_id,
                    mt_pct_used: Math.round((u.mt_chars_used / TIER_MT[tier]) * 100),
                    ai_pct_used: Math.round((u.ai_queries_used / TIER_AI[tier]) * 100),
                    tier,
                };
            });

        setInsights({
            totalProjects: projects.data?.length || 0,
            activeUsers: new Set(projects.data?.map((p) => p.owner_id)).size,
            languagePairs,
            tmStats,
            preAOStats: Object.values(aoMap),
            usersNearLimit,
            providerBreakdown: {
                deepl_total,
                google_total,
                mymemory_total,
                grand_total,
                deepl_pct: grand_total
                    ? Math.round((deepl_total / grand_total) * 100)
                    : 0,
                google_pct: grand_total
                    ? Math.round((google_total / grand_total) * 100)
                    : 0,
                mymemory_pct: grand_total
                    ? Math.round((mymemory_total / grand_total) * 100)
                    : 0,
            },
        });
    };

    const updateTier = async (userId: string, tier: string) => {
        setUpdating(userId);
        await supabase.from("profiles").update({ tier }).eq("id", userId);
        setProfiles((prev) =>
            prev.map((p) => (p.id === userId ? { ...p, tier } : p)),
        );
        setUpdating(null);
    };

    const getUsageForUser = (userId: string) =>
        usage.find((u) => u.user_id === userId);

    const tierColor = (tier: string) => {
        if (tier === "premium")
            return "bg-gold-100 text-gold-800 border-gold-300";
        if (tier === "standard")
            return "bg-blue-100 text-blue-800 border-blue-300";
        return "bg-slate-100 text-slate-700 border-slate-300";
    };

    if (loading)
        return (
            <div className="min-h-screen bg-slate-900 flex items-center justify-center">
                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gold-500" />
            </div>
        );

    return (
        <div className="min-h-screen bg-slate-50">
            {/* Header */}
            <div className="bg-slate-900 px-6 py-4 flex items-center justify-between">
                <div className="flex items-center gap-3">
                    <Shield className="w-6 h-6 text-gold-500" />
                    <h1 className="text-xl font-bold text-white">
                        TranslyxCAT Admin
                    </h1>
                </div>
                <button
                    onClick={loadData}
                    className="flex items-center gap-2 px-3 py-1.5 bg-slate-800 hover:bg-slate-700 text-slate-300 rounded-lg text-sm border border-slate-600 transition-colors"
                >
                    <RefreshCw className="w-3.5 h-3.5" />
                    Refresh
                </button>
            </div>

            {/* Stats bar */}
            <div className="bg-white border-b border-slate-200 px-6 py-4 grid grid-cols-4 gap-6">
                <div className="text-center">
                    <div className="text-2xl font-bold text-slate-900">
                        {profiles.length}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                        Total Users
                    </div>
                </div>
                <div className="text-center">
                    <div className="text-2xl font-bold text-gold-600">
                        {profiles.filter((p) => p.tier === "premium").length}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">Premium</div>
                </div>
                <div className="text-center">
                    <div className="text-2xl font-bold text-blue-600">
                        {profiles.filter((p) => p.tier === "standard").length}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">Standard</div>
                </div>
                <div className="text-center">
                    <div className="text-2xl font-bold text-slate-600">
                        {feedback.length}
                    </div>
                    <div className="text-xs text-slate-500 mt-1">
                        Feedback Items
                    </div>
                </div>
            </div>

            {/* Tabs */}
            <div className="bg-white border-b border-slate-200 px-6">
                <div className="flex gap-1">
                    {(["users", "usage", "feedback", "insights"] as const).map(
                        (t) => (
                            <button
                                key={t}
                                onClick={() => setTab(t)}
                                className={`px-4 py-3 text-sm font-medium border-b-2 transition-colors capitalize ${
                                    tab === t
                                        ? "border-gold-500 text-gold-600"
                                        : "border-transparent text-slate-500 hover:text-slate-700"
                                }`}
                            >
                                {t === "users" && (
                                    <Users className="w-4 h-4 inline mr-1.5" />
                                )}
                                {t === "usage" && (
                                    <BarChart3 className="w-4 h-4 inline mr-1.5" />
                                )}
                                {t === "feedback" && (
                                    <MessageSquare className="w-4 h-4 inline mr-1.5" />
                                )}
                                {t === "insights" && (
                                    <TrendingUp className="w-4 h-4 inline mr-1.5" />
                                )}
                                {t}
                            </button>
                        ),
                    )}
                </div>
            </div>

            {/* Content */}
            <div className="px-6 py-6">
                {/* USERS TAB */}
                {tab === "users" && (
                    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr>
                                    <th className="text-left px-4 py-3 font-medium text-slate-600">
                                        User
                                    </th>
                                    <th className="text-left px-4 py-3 font-medium text-slate-600">
                                        Joined
                                    </th>
                                    <th className="text-left px-4 py-3 font-medium text-slate-600">
                                        Tier
                                    </th>
                                    <th className="text-left px-4 py-3 font-medium text-slate-600">
                                        Change Tier
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {profiles.map((profile) => (
                                    <tr
                                        key={profile.id}
                                        className="hover:bg-slate-50"
                                    >
                                        <td className="px-4 py-3">
                                            <div className="font-medium text-slate-900">
                                                {profile.full_name || "—"}
                                            </div>
                                            <div className="text-xs text-slate-500">
                                                {profile.email}
                                            </div>
                                        </td>
                                        <td className="px-4 py-3 text-slate-500">
                                            {new Date(
                                                profile.created_at,
                                            ).toLocaleDateString()}
                                        </td>
                                        <td className="px-4 py-3">
                                            <span
                                                className={`px-2 py-0.5 rounded-full text-xs font-medium border ${tierColor(profile.tier)}`}
                                            >
                                                {profile.tier}
                                            </span>
                                        </td>
                                        <td className="px-4 py-3">
                                            <select
                                                value={profile.tier}
                                                disabled={
                                                    updating === profile.id
                                                }
                                                onChange={(e) =>
                                                    updateTier(
                                                        profile.id,
                                                        e.target.value,
                                                    )
                                                }
                                                className="text-xs border border-slate-200 rounded-lg px-2 py-1 bg-white focus:outline-none focus:ring-2 focus:ring-gold-500"
                                            >
                                                <option value="free">
                                                    Free
                                                </option>
                                                <option value="standard">
                                                    Standard
                                                </option>
                                                <option value="premium">
                                                    Premium
                                                </option>
                                            </select>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* USAGE TAB */}
                {tab === "usage" && (
                    <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                        <table className="w-full text-sm">
                            <thead className="bg-slate-50 border-b border-slate-200">
                                <tr>
                                    <th className="text-left px-4 py-3 font-medium text-slate-600">
                                        User
                                    </th>
                                    <th className="text-left px-4 py-3 font-medium text-slate-600">
                                        DeepL
                                    </th>
                                    <th className="text-left px-4 py-3 font-medium text-slate-600">
                                        Google
                                    </th>
                                    <th className="text-left px-4 py-3 font-medium text-slate-600">
                                        MyMemory
                                    </th>
                                    <th className="text-left px-4 py-3 font-medium text-slate-600">
                                        AI Queries
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100">
                                {profiles.map((profile) => {
                                    const u = getUsageForUser(profile.id);
                                    return (
                                        <tr
                                            key={profile.id}
                                            className="hover:bg-slate-50"
                                        >
                                            <td className="px-4 py-3">
                                                <div className="font-medium text-slate-900">
                                                    {profile.email}
                                                </div>
                                            </td>
                                            <td className="px-4 py-3 text-slate-700">
                                                {u
                                                    ? u.mt_chars_deepl?.toLocaleString() ||
                                                      0
                                                    : "—"}
                                            </td>
                                            <td className="px-4 py-3 text-slate-700">
                                                {u
                                                    ? u.mt_chars_google?.toLocaleString() ||
                                                      0
                                                    : "—"}
                                            </td>
                                            <td className="px-4 py-3 text-slate-700">
                                                {u
                                                    ? u.mt_chars_mymemory?.toLocaleString() ||
                                                      0
                                                    : "—"}
                                            </td>
                                            <td className="px-4 py-3 text-slate-700">
                                                {u ? u.ai_queries_used : "—"}
                                                                </td>
                                                                <td className="px-3 py-2 text-xs text-gray-700 text-right">
                                                                  {u?.storage_used_bytes
                                                                    ? u.storage_used_bytes > 1048576
                                                                      ? `${(u.storage_used_bytes / 1048576).toFixed(1)} MB`
                                                                      : `${(u.storage_used_bytes / 1024).toFixed(0)} KB`
                                                                    : "0 KB"}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}

                {/* FEEDBACK TAB */}
                {tab === "feedback" && (
                    <div className="space-y-3">
                        {feedback.length === 0 && (
                            <div className="text-center py-12 text-slate-400">
                                No feedback yet
                            </div>
                        )}
                        {feedback.map((item) => (
                            <div
                                key={item.id}
                                className="bg-white rounded-xl border border-slate-200 p-4"
                            >
                                <div className="flex items-center justify-between mb-2">
                                    <span className="text-xs font-medium text-slate-500">
                                        {profiles.find(
                                            (p) => p.id === item.user_id,
                                        )?.email || item.user_id}
                                    </span>
                                    <span className="text-xs text-slate-400">
                                        {new Date(
                                            item.created_at,
                                        ).toLocaleString()}
                                    </span>
                                </div>
                                <p className="text-sm text-slate-800">
                                    {item.message}
                                </p>
                                {item.page && (
                                    <span className="mt-2 inline-block text-xs px-2 py-0.5 bg-slate-100 text-slate-500 rounded">
                                        {item.page}
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                )}

                {tab === "insights" && (
                    <div className="space-y-6">
                        {!insights ? (
                            <div className="text-center py-12">
                                <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-gold-500 mx-auto mb-4" />
                                <div className="text-slate-500">
                                    Loading insights...
                                </div>
                            </div>
                        ) : (
                            <>
                                {/* Top stats */}
                                <div className="grid grid-cols-4 gap-4">
                                    <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
                                        <div className="text-3xl font-bold text-slate-900">
                                            {insights.totalProjects}
                                        </div>
                                        <div className="text-xs text-slate-500 mt-1">
                                            Total Projects
                                        </div>
                                    </div>
                                    <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
                                        <div className="text-3xl font-bold text-gold-600">
                                            {insights.activeUsers}
                                        </div>
                                        <div className="text-xs text-slate-500 mt-1">
                                            Active Users
                                        </div>
                                    </div>
                                    <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
                                        <div className="text-3xl font-bold text-blue-600">
                                            {insights.tmStats.total_entries.toLocaleString()}
                                        </div>
                                        <div className="text-xs text-slate-500 mt-1">
                                            TM Entries
                                        </div>
                                    </div>
                                    <div className="bg-white rounded-xl border border-slate-200 p-4 text-center">
                                        <div className="text-3xl font-bold text-emerald-600">
                                            {insights.tmStats.added_last_7_days}
                                        </div>
                                        <div className="text-xs text-slate-500 mt-1">
                                            TM Added (7d)
                                        </div>
                                    </div>
                                </div>

                                {/* Language pairs + Pre-AO */}
                                <div className="grid grid-cols-2 gap-4">
                                    <div className="bg-white rounded-xl border border-slate-200 p-4">
                                        <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                                            <Globe className="w-4 h-4 text-gold-500" />
                                            Language Pairs
                                        </h3>
                                        <div className="space-y-2">
                                            {insights.languagePairs.map(
                                                (lp, i) => (
                                                    <div
                                                        key={i}
                                                        className="flex items-center justify-between text-sm"
                                                    >
                                                        <span className="text-slate-700 font-medium">
                                                            {lp.source_language.toUpperCase()}{" "}
                                                            →{" "}
                                                            {lp.target_language.toUpperCase()}
                                                        </span>
                                                        <div className="flex items-center gap-3">
                                                            <span className="text-slate-500">
                                                                {lp.total_words.toLocaleString()}{" "}
                                                                words
                                                            </span>
                                                            <span className="bg-gold-100 text-gold-800 text-xs px-2 py-0.5 rounded-full font-medium">
                                                                {
                                                                    lp.project_count
                                                                }{" "}
                                                                projects
                                                            </span>
                                                        </div>
                                                    </div>
                                                ),
                                            )}
                                        </div>
                                    </div>
                                    <div className="bg-white rounded-xl border border-slate-200 p-4">
                                        <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                                            <Database className="w-4 h-4 text-gold-500" />
                                            Orthographic Standard
                                        </h3>
                                        <div className="space-y-3">
                                            {insights.preAOStats.map((s, i) => {
                                                const total =
                                                    insights.preAOStats.reduce(
                                                        (sum, x) =>
                                                            sum +
                                                            x.project_count,
                                                        0,
                                                    );
                                                const pct = Math.round(
                                                    (s.project_count / total) *
                                                        100,
                                                );
                                                return (
                                                    <div key={i}>
                                                        <div className="flex justify-between text-sm mb-1">
                                                            <span className="font-medium text-slate-700">
                                                                {s.orthographic_standard ===
                                                                "pre_ao"
                                                                    ? "Pre-AO (Angola/Mozambique)"
                                                                    : "Post-AO 2009 (Standard)"}
                                                            </span>
                                                            <span className="text-slate-500">
                                                                {
                                                                    s.project_count
                                                                }{" "}
                                                                projects ({pct}
                                                                %)
                                                            </span>
                                                        </div>
                                                        <div className="w-full bg-slate-100 rounded-full h-2">
                                                            <div
                                                                className={`h-2 rounded-full ${s.orthographic_standard === "pre_ao" ? "bg-gold-500" : "bg-blue-500"}`}
                                                                style={{
                                                                    width: `${pct}%`,
                                                                }}
                                                            />
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>

                                {/* MT Provider breakdown */}
                                <div className="bg-white rounded-xl border border-slate-200 p-4">
                                    <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                                        <Zap className="w-4 h-4 text-gold-500" />
                                        MT Provider Usage
                                    </h3>
                                    <div className="grid grid-cols-3 gap-4">
                                        {[
                                            {
                                                name: "DeepL",
                                                total: insights
                                                    .providerBreakdown
                                                    .deepl_total,
                                                pct: insights.providerBreakdown
                                                    .deepl_pct,
                                                color: "bg-blue-500",
                                            },
                                            {
                                                name: "Google",
                                                total: insights
                                                    .providerBreakdown
                                                    .google_total,
                                                pct: insights.providerBreakdown
                                                    .google_pct,
                                                color: "bg-emerald-500",
                                            },
                                            {
                                                name: "MyMemory",
                                                total: insights
                                                    .providerBreakdown
                                                    .mymemory_total,
                                                pct: insights.providerBreakdown
                                                    .mymemory_pct,
                                                color: "bg-slate-500",
                                            },
                                        ].map((p) => (
                                            <div
                                                key={p.name}
                                                className="text-center"
                                            >
                                                <div className="text-lg font-bold text-slate-900">
                                                    {p.total.toLocaleString()}
                                                </div>
                                                <div className="text-xs text-slate-500 mb-2">
                                                    {p.name} chars
                                                </div>
                                                <div className="w-full bg-slate-100 rounded-full h-2">
                                                    <div
                                                        className={`h-2 rounded-full ${p.color}`}
                                                        style={{
                                                            width: `${p.pct}%`,
                                                        }}
                                                    />
                                                </div>
                                                <div className="text-xs text-slate-400 mt-1">
                                                    {p.pct}% of total
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    <div className="mt-3 pt-3 border-t border-slate-100 text-center text-xs text-slate-400">
                                        Total MT usage:{" "}
                                        {insights.providerBreakdown.grand_total.toLocaleString()}{" "}
                                        characters
                                    </div>
                                </div>

                                {/* Users near limit */}
                                {insights.usersNearLimit.length > 0 && (
                                    <div className="bg-white rounded-xl border border-orange-200 p-4">
                                        <h3 className="text-sm font-semibold text-orange-700 mb-3">
                                            ⚠ Users Approaching Limits
                                        </h3>
                                        <div className="space-y-2">
                                            {insights.usersNearLimit.map(
                                                (u, i) => (
                                                    <div
                                                        key={i}
                                                        className="flex items-center justify-between text-sm p-2 bg-orange-50 rounded-lg"
                                                    >
                                                        <span className="font-medium text-slate-700">
                                                            {u.email}
                                                        </span>
                                                        <div className="flex items-center gap-4">
                                                            <span
                                                                className={`text-xs font-medium ${u.mt_pct_used > 80 ? "text-red-600" : "text-orange-600"}`}
                                                            >
                                                                MT:{" "}
                                                                {u.mt_pct_used}%
                                                            </span>
                                                            <span
                                                                className={`text-xs font-medium ${u.ai_pct_used > 80 ? "text-red-600" : "text-orange-600"}`}
                                                            >
                                                                AI:{" "}
                                                                {u.ai_pct_used}%
                                                            </span>
                                                        </div>
                                                    </div>
                                                ),
                                            )}
                                        </div>
                                    </div>
                                )}
                            </>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default AdminPage;
